/**
 * This slice is for shared global state, which can be used by other redux
 * slices. This slice shouldn't import other actions, but other slices can
 * import actions from this slice.
 */

import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { Language, getLang } from "@src/i18n";

export interface Config {
  bolUrl: string;
  companiesUrl: string;
  gtmId: string | null;
  portalUrl: string;
  sentryDsn: string | null;
  supportUrl: string;
  vgUrl: string;
  zeckitUrl: string;
  lk2Url: string;
}

export type OrgId = string;

interface CommonState {
  config: Config | null;
  language: Language;
  hasOrgContext: boolean;
  selectedOrgId: OrgId | null;
  sessionId: string;
}

const initialAppLanguage = getLang();
document.documentElement.lang = initialAppLanguage; // https://web.dev/html-has-lang

const initialState: CommonState = {
  config: null,
  language: initialAppLanguage,
  hasOrgContext: false,
  selectedOrgId: null,
  sessionId: Math.random().toString(36).substr(2, 9),
};

export const commonSlice = createSlice({
  name: "common",
  initialState,
  reducers: {
    changeLanguage: {
      reducer: (state, action: PayloadAction<Language>) => {
        state.language = action.payload;
      },
      prepare: (newLanguage: Language) => {
        // Set html element lang for accessibility
        document.documentElement.lang = newLanguage;

        return { payload: newLanguage };
      },
    },
    changeOrg: (
      state,
      action: PayloadAction<{ orgId: OrgId; ifPrevious?: OrgId | null }>
    ) => {
      if (action.payload.ifPrevious === undefined) {
        state.selectedOrgId = action.payload.orgId;
      } else if (action.payload.ifPrevious === state.selectedOrgId) {
        state.selectedOrgId = action.payload.orgId;
      }
    },
    setConfig: (state, action: PayloadAction<Config>) => {
      state.config = action.payload;
    },
    toggleOrgContext: (state, action: PayloadAction<boolean>) => {
      state.hasOrgContext = action.payload;
    },
  },
});

export const {
  changeLanguage,
  changeOrg,
  setConfig,
  toggleOrgContext,
} = commonSlice.actions;

export default commonSlice.reducer;
